import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Eye, EyeOff, ArrowLeft, CalendarIcon, Camera, User, Upload, Heart } from 'lucide-react';
import { Footer } from '../components/Footer';
import SEO, { pageSEO } from '../components/SEO';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Floating Gold Hearts Component
const FloatingGoldHearts = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
    {[...Array(12)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          y: [0, -20, 0],
          opacity: [0.5, 0.8, 0.5],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 4 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      >
        <Heart 
          className="text-[#B8860B] fill-[#D4AF37]" 
          style={{ 
            width: `${16 + Math.random() * 20}px`,
            height: `${16 + Math.random() * 20}px`,
            opacity: 0.6 + Math.random() * 0.3
          }} 
        />
      </motion.div>
    ))}
  </div>
);

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    birth_date: null,
    bio: '',
    looking_for: 'connection',
    phone: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const fileInputRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.birth_date) {
      toast.error('Please select your birth date');
      return;
    }
    setIsLoading(true);
    try {
      // First, upload the image if there is one
      let avatarUrl = null;
      if (profileImage) {
        const imageFormData = new FormData();
        imageFormData.append('file', profileImage);
        try {
          const uploadResponse = await axios.post(`${API}/upload/image`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          avatarUrl = uploadResponse.data.url;
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          // Continue without image
        }
      }

      // Register the user
      await register({
        ...formData,
        birth_date: format(formData.birth_date, 'yyyy-MM-dd'),
        avatar_url: avatarUrl
      });
      toast.success('Welcome to Intuitive Matchmaker! Your profile has been created.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen swirl-bg relative overflow-hidden">
      <SEO {...pageSEO.register} />
      <FloatingGoldHearts />
      
      {/* Main Content - Centered */}
      <div className="flex items-center justify-center p-6 min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-lg relative z-10"
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#8B5CF6] hover:text-[#D4AF37] mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wide font-semibold">Back to Home</span>
          </Link>

          <div className="ethereal-card rounded-3xl p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 mb-4 glow-purple">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-mystical text-2xl sm:text-3xl text-[#2D1B3D] mb-2 font-bold uppercase tracking-wide" style={{ textShadow: '0.5px 0 0 currentColor, -0.5px 0 0 currentColor' }}>Join the Free Numerology Matchmaking Database</h1>
              <p className="text-[#6D5A7D]">Create your profile to be matched with your destined love</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Profile Picture Upload */}
              <div className="flex justify-center mb-6">
              <div className="relative">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-28 h-28 rounded-full border-4 border-[#D4AF37] overflow-hidden cursor-pointer hover:opacity-80 transition-opacity bg-gradient-to-br from-[#8B5CF6]/20 to-[#EC4899]/20 flex items-center justify-center"
                  data-testid="profile-image-upload"
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Profile preview" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-[#8B5CF6]" />
                  )}
                </div>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#D4AF37] flex items-center justify-center cursor-pointer hover:scale-110 transition-transform shadow-lg"
                >
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  data-testid="profile-image-input"
                />
              </div>
            </div>
            <p className="text-center text-sm text-[#8B5CF6] -mt-2 mb-4">Click to upload your photo</p>

            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-[#3D2A4D] font-semibold">Full Birth Name</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                placeholder="As it appears on your birth certificate"
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-12"
                data-testid="register-name-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#3D2A4D] font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="your@email.com"
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-12"
                data-testid="register-email-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#3D2A4D] font-semibold">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-12"
                data-testid="register-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#3D2A4D] font-semibold">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-12 pr-12"
                  data-testid="register-password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8B5CF6] hover:text-[#D4AF37]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#3D2A4D] font-semibold">Birth Date</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-12 justify-start text-left font-normal bg-white/70 border-purple-200 hover:border-[#8B5CF6] hover:bg-white/90 rounded-xl"
                    data-testid="register-birthdate-trigger"
                  >
                    <CalendarIcon className="mr-3 h-4 w-4 text-[#8B5CF6]" />
                    {formData.birth_date ? (
                      <span className="text-[#2D1B3D]">{format(formData.birth_date, 'MMMM d, yyyy')}</span>
                    ) : (
                      <span className="text-[#8C7A9C]">Select your birth date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-purple-200" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.birth_date}
                    onSelect={(date) => {
                      handleChange('birth_date', date);
                      setCalendarOpen(false);
                    }}
                    disabled={(date) => date > new Date() || date < new Date('1920-01-01')}
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={1920}
                    toYear={new Date().getFullYear()}
                    data-testid="register-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-[#3D2A4D] font-semibold">What Are You Looking For?</Label>
              <Select 
                value={formData.looking_for} 
                onValueChange={(value) => handleChange('looking_for', value)}
              >
                <SelectTrigger 
                  className="h-12 bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl"
                  data-testid="register-looking-for-select"
                >
                  <SelectValue placeholder="What are you seeking?" />
                </SelectTrigger>
                <SelectContent className="bg-white border-purple-200">
                  <SelectItem value="love">Romantic Love / Soulmate</SelectItem>
                  <SelectItem value="connection">Spiritual Connection</SelectItem>
                  <SelectItem value="friendship">Kindred Spirits / Friendship</SelectItem>
                  <SelectItem value="guidance">Life Guidance / Clarity</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio" className="text-[#3D2A4D] font-semibold">Tell Us About Yourself</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                placeholder="Share a bit about yourself, your interests, and what you're looking for in a partner..."
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl resize-none"
                rows={4}
                data-testid="register-bio-input"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 rounded-full btn-magic text-white font-bold tracking-wide mt-6"
              data-testid="register-submit-btn"
            >
              {isLoading ? 'Creating Your Profile...' : 'Create My Profile'}
            </Button>
          </form>

          <p className="text-center mt-8 text-[#6D5A7D]">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-[#8B5CF6] hover:text-[#D4AF37] font-bold"
              data-testid="login-link"
            >
              Sign In
            </Link>
          </p>
        </div>
      </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default RegisterPage;
